import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  IconButton,
  InputAdornment,
  Link,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { authAPI } from '../../services/api';

const RESEND_COOLDOWN_SECONDS = 30;

function Login() {
  const navigate = useNavigate();
  const [step, setStep] = useState('credentials'); // 'credentials' | 'otp'
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [otp, setOtp] = useState('');
  const [pendingLoginId, setPendingLoginId] = useState('');
  const [mobileHint, setMobileHint] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownTimerRef = useRef(null);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    cooldownTimerRef.current = setInterval(() => {
      setResendCooldown((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(cooldownTimerRef.current);
  }, [resendCooldown > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  const proceedWithLoggedInUser = (access_token, user) => {
    if (user.role !== 'ADMIN' && user.role !== 'TALUKA_ADMIN' && user.role !== 'SUPERVISOR' && user.role !== 'ANM') {
      setError('Access denied. Admin privileges required.');
      setLoading(false);
      return;
    }

    localStorage.setItem('admin_token', access_token);
    localStorage.setItem('admin_user', JSON.stringify(user));
    navigate(user.role === 'ANM' ? '/admin/anm/approvals' : '/admin/dashboard');
  };

  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authAPI.requestOtp(username, password);
      const { pending_login_id, mobile_hint } = response.data;
      setPendingLoginId(pending_login_id);
      setMobileHint(mobile_hint);
      setOtp('');
      setStep('otp');
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setLoading(false);
    } catch (err) {
      console.error('OTP request error:', err);
      setError(err.response?.data?.detail || err.message || 'Sign in failed. Please try again.');
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authAPI.verifyOtp(pendingLoginId, otp);
      const { access_token, user } = response.data;
      proceedWithLoggedInUser(access_token, user);
    } catch (err) {
      console.error('OTP verify error:', err);
      setError(err.response?.data?.detail || err.message || 'Invalid OTP. Please try again.');
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setResendLoading(true);
    try {
      await authAPI.resendOtp(pendingLoginId);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      console.error('OTP resend error:', err);
      setError(err.response?.data?.detail || err.message || 'Could not resend OTP.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleBackToCredentials = () => {
    setStep('credentials');
    setOtp('');
    setPendingLoginId('');
    setError('');
    setResendCooldown(0);
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            ZP ASHA Admin
          </Typography>
          <Typography variant="body2" align="center" color="textSecondary" sx={{ mb: 3 }}>
            {step === 'credentials' ? 'Sign in to access the admin portal' : `Enter the OTP sent to ${mobileHint}`}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {step === 'credentials' ? (
            <Box component="form" onSubmit={handleCredentialsSubmit}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="username"
                label="Email or Phone Number"
                name="username"
                autoComplete="username"
                autoFocus
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter email or 10-digit phone number"
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? 'पासवर्ड लपवा' : 'पासवर्ड दाखवा'}
                        onClick={() => setShowPassword((v) => !v)}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                {loading ? 'Sending OTP...' : 'Send OTP'}
              </Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleOtpSubmit}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="otp"
                label="OTP"
                name="otp"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading || !otp}
              >
                {loading ? 'Verifying...' : 'Verify & Sign In'}
              </Button>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Link component="button" type="button" variant="body2" onClick={handleBackToCredentials}>
                  मागे जा
                </Link>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={handleResend}
                  disabled={resendCooldown > 0 || resendLoading}
                  sx={{ color: resendCooldown > 0 ? 'text.disabled' : undefined }}
                >
                  {resendCooldown > 0 ? `OTP पुन्हा पाठवा (${resendCooldown}s)` : 'OTP पुन्हा पाठवा'}
                </Link>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
}

export default Login;
